import { useState, useEffect, useCallback } from 'react';
import {
  MONTH_LABEL_WIDTH,
  HEADER_HEIGHT,
  TOTAL_COLUMNS,
  CELL_WIDTH,
  CELL_MIN_HEIGHT,
  INITIAL_ZOOM_MIN,
  INITIAL_ZOOM_MAX,
  MIN_ZOOM,
  MAX_ZOOM,
  ZOOM_DELTA
} from '../constants/layout';

/** Estimated scrollbar width in pixels (macOS: 15px, Windows: 17px typical) */
const SCROLLBAR_WIDTH = 15;

/** Calendar content width (unscaled): month label + (columns * cell width) */
const CONTENT_WIDTH = MONTH_LABEL_WIDTH + TOTAL_COLUMNS * CELL_WIDTH;

/** Estimated content height: header + 12 months * min cell height */
const ESTIMATED_CONTENT_HEIGHT = HEADER_HEIGHT + 12 * CELL_MIN_HEIGHT;

/** Approximate header height for viewport calculations */
const APP_HEADER_HEIGHT = 60;

/**
 * Calculate the minimum zoom level that fills the viewport width.
 */
function calculateMinZoomForViewport() {
  const fullViewportWidth = document.documentElement.clientWidth;
  const viewportWidth = fullViewportWidth - SCROLLBAR_WIDTH;
  const minZoom = (viewportWidth / CONTENT_WIDTH) * 100;
  return Math.max(MIN_ZOOM, Math.floor(minZoom));
}

/**
 * Calculate initial zoom level to fit calendar width to viewport.
 */
function calculateInitialZoom() {
  const fullViewportWidth = document.documentElement.clientWidth;
  const initialZoom = (fullViewportWidth / CONTENT_WIDTH) * 100;
  const clampedZoom = Math.min(INITIAL_ZOOM_MAX, Math.max(INITIAL_ZOOM_MIN, initialZoom));
  const scaleFactor = clampedZoom / 100;
  
  const availableHeight = window.innerHeight - APP_HEADER_HEIGHT;
  const scaledContentHeight = ESTIMATED_CONTENT_HEIGHT * scaleFactor;
  const willHaveVerticalScrollbar = scaledContentHeight > availableHeight;
  
  const viewportWidth = willHaveVerticalScrollbar 
    ? fullViewportWidth - SCROLLBAR_WIDTH 
    : fullViewportWidth;
  
  const optimalZoom = (viewportWidth / CONTENT_WIDTH) * 100;
  const roundedZoom = Math.floor(optimalZoom);
  return Math.min(INITIAL_ZOOM_MAX, Math.max(INITIAL_ZOOM_MIN, roundedZoom));
}

/**
 * Custom hook for managing zoom and pan state for the calendar.
 * 
 * @param {React.RefObject} containerRef - Reference to the scrollable container element
 * @returns {Object} Zoom and pan state and handlers
 */
function useZoomPan(containerRef) {
  const [zoom, setZoom] = useState(() => calculateInitialZoom());
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [scrollStart, setScrollStart] = useState({ x: 0, y: 0 });

  const scaleFactor = zoom / 100;

  // Mouse panning handlers
  const handleMouseDown = useCallback((e) => {
    if (e.button !== 1) return;
    if (e.target.closest('.calendar__year-nav')) return;
    if (e.target.closest('.calendar__year-label')) return;
    
    const container = containerRef.current;
    if (!container) return;

    setIsPanning(true);
    setPanStart({ x: e.clientX, y: e.clientY });
    setScrollStart({ x: container.scrollLeft, y: container.scrollTop });
    e.preventDefault();
  }, [containerRef]);

  const handleMouseMove = useCallback((e) => {
    if (!isPanning) return;
    
    const container = containerRef.current;
    if (!container) return;

    const deltaX = e.clientX - panStart.x;
    const deltaY = e.clientY - panStart.y;
    
    container.scrollLeft = scrollStart.x - deltaX;
    container.scrollTop = scrollStart.y - deltaY;
  }, [isPanning, panStart, scrollStart, containerRef]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  // Attach global mouse listeners when panning
  useEffect(() => {
    if (isPanning) {
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('mousemove', handleMouseMove);
      return () => {
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, [isPanning, handleMouseUp, handleMouseMove]);

  // Handle mouse wheel zoom
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -ZOOM_DELTA : ZOOM_DELTA;
        const minZoom = calculateMinZoomForViewport();
        setZoom((prev) => Math.min(MAX_ZOOM, Math.max(minZoom, prev + delta)));
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [containerRef]);

  // Handle window resize - adjust zoom if below minimum
  useEffect(() => {
    const handleResize = () => {
      const minZoom = calculateMinZoomForViewport();
      setZoom((prev) => {
        if (prev < minZoom) {
          return minZoom;
        }
        return prev;
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return {
    zoom,
    scaleFactor,
    isPanning,
    handleMouseDown
  };
}

export default useZoomPan;
